/*
 * File: beautifulDayMonsterApi.ts
 * Project: beaker
 * -----
 * Last Modified: 15 Mar 2022 11:16:03 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { CustomerNameModel } from 'models/CustomerNameModel';
import { ReduxState } from 'redux/store';

export interface GetCustomersParams {
  dataAreaId: string | null;
  customerNameContains?: string;
  customerAccountContains?: string;
  orderByField?: string;
  offset: number;
}

export interface GetCustomersResponseModel {
  data: CustomerNameModel[];
  totalCount: number;
}

export interface GetBuyersParams {
  dataAreaId: string | null;
  buyerNameContains?: string;
  offset: number;
  customerId: string;
}

export interface CreateBuyerParams {
  buyerData: {
    dataAreaId: string;
    firstName: string;
    lastName: string;
  };
  customerId: string;
}

export interface CreateBuyerResponseModel {
  buyerId: string;
  buyerName: string;
  emailAddress: string;
  phoneNumber: string;
}
export interface GetPaymentTermsParams {
  dataAreaId: string | null;
  paymentTermContains?: string;
  offset: number;
}

const beautifulDayMonsterBaseUrl = process.env.REACT_APP_BEAUTIFUL_DAY_MONSTER_BASE_URL || '';
const functionsKey = process.env.REACT_APP_API_HOST_KEY_BEAUTIFUL_DAY_MONSTER || '';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: beautifulDayMonsterBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const beautifulDayMonsterApi = createApi({
  reducerPath: 'customerApi',
  baseQuery: rawBaseQuery,
  endpoints: (builder) => ({
    getCustomers: builder.query<GetCustomersResponseModel, GetCustomersParams>({
      query: ({ dataAreaId, customerNameContains, customerAccountContains, orderByField, offset }) => ({
        url: `customers`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : '',
          customerNameContains: customerNameContains ? encodeValue({ isUrl: false, value: customerNameContains }) : undefined,
          customerAccountContains: customerAccountContains ? encodeValue({ isUrl: false, value: customerAccountContains }) : undefined,
          orderByField,
          offset
        }
      }),
      onQueryStarted: async (_arg, _api) => {
        try {
          await _api.queryFulfilled;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().customerApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.dispatch(
              beautifulDayMonsterApi.util.updateQueryData('getCustomers', _arg, (draft) => {
                draft.data = [...(lastQueryData.data as GetCustomersResponseModel | any).data, ...draft.data];
              })
            );
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: 'diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetCustomersQuery, useGetVersionQuery, useGetApiNameQuery } = beautifulDayMonsterApi;
